import type { HTMLAttributes, ReactNode } from 'react';
import './misc.css';
import { useTranslation } from 'react-i18next';

export { Button } from './Button';
export type { ButtonProps, ButtonSize, ButtonVariant } from './Button';
export { Input, Textarea } from './Input';
export { TagPicker } from './TagPicker';
export { MoreMenu, type MoreMenuItem } from './MoreMenu';
export { SignaturePad } from './SignaturePad';
export { ConfirmProvider, useConfirm, type ConfirmAsk } from './ConfirmDialog';
export type { InputProps, TextareaProps } from './Input';

/** A surface with a border. The default container for anything on a page. */
export function Card({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={['card', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="card__header">
      <div className="card__heading">
        <h2 className="card__title">{title}</h2>
        {subtitle ? <p className="card__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="card__actions">{actions}</div> : null}
    </header>
  );
}

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand';

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * Shown while data loads.
 *
 * A skeleton rather than a spinner: it keeps the layout at its final size, so
 * the page does not jump when the data arrives.
 */
export function Skeleton({
  width = '100%',
  height = 16,
}: {
  width?: string | number;
  height?: number;
}) {
  return <span className="skeleton" style={{ width, height }} aria-hidden />;
}

export function Spinner({ size = 16 }: { size?: number }) {
  const { t } = useTranslation('common');
  return (
    <span
      className="spinner"
      style={{ width: size, height: size }}
      role="status"
      aria-label={t('seIncarca2')}
    />
  );
}

/**
 * Shown where a list would be, when the list is empty.
 *
 * Always says what would appear here and what to do about it — "Nicio
 * înregistrare" on its own tells the user nothing they did not already see.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  fill,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  /** Grow to fill the parent and centre vertically, so a short tab leaves no
      void beneath the card. The parent must be a flex column. */
  fill?: boolean;
}) {
  return (
    <div className={fill ? 'empty empty--fill' : 'empty'}>
      {icon ? <div className="empty__icon">{icon}</div> : null}
      <p className="empty__title">{title}</p>
      {description ? <p className="empty__desc">{description}</p> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}

/**
 * Shown when a request failed.
 *
 * Carries the request id when there is one: it is the only thing that lets
 * support find the matching server log.
 */
export function ErrorState({
  title = 'Ceva nu a mers bine',
  message,
  requestId,
  onRetry,
}: {
  title?: string;
  message: string;
  requestId?: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="errorstate" role="alert">
      <p className="errorstate__title">{title}</p>
      <p className="errorstate__msg">{message}</p>
      {requestId ? (
        <code className="errorstate__id">
          {t('cod')} {requestId}
        </code>
      ) : null}
      {onRetry ? (
        <button type="button" className="errorstate__retry" onClick={onRetry}>
          {t('incearcaDinNou')}
        </button>
      ) : null}
    </div>
  );
}
